import i18n from '@/plugins/vue-i18n';

const { t } = i18n.global;

export enum RiskLevel {
  high = 'HIGH',
  medium = 'MED',
  low = 'LOW',
}

export const VendorType = {
  boq: {
    key: 'BOQPAY',
    name: t('vendors.filters.boq'),
  },
  custom: {
    key: 'CUSTOM',
    name: t('vendors.filters.custom'),
  },
};

/**
 * Size cap on a vendor-item document, shared by the create form and the edit drawer so the limit
 * and the copy that quotes it can't drift apart. Wider than the legacy form, which enforced no
 * client-side limit at all — align it with the API's real cap if that differs.
 */
export const VENDOR_ITEM_DOCUMENT_MAX_SIZE_MB = 10;

export enum TemplateType {
  buildIt = 'BuildIt',
  boqStandard = 'BoqStandard',
}

export enum Status {
  approved = 'Approved',
  // Context: display only. No API status carries it: an Approved request whose payment awaits its
  // proof shows it (see `paymentRequestBadgeStatus`).
  awaitingProof = 'AwaitingProof',
  cancelled = 'Cancelled',
  closed = 'Closed',
  complete = 'Complete',
  credit = 'Credit',
  current = 'Current',
  declined = 'Declined',
  delivered = 'Delivered',
  draft = 'Draft',
  expired = 'Expired',
  live = 'Live',
  na = 'NA',
  paid = 'Paid',
  partialDelivered = 'PartiallyDelivered',
  partialPaid = 'Partially_Paid',
  pending = 'Pending',
  planning = 'Planning',
  prsPending = 'PRs Pending',
  rejected = 'Rejected',
  requested = 'Requested',
  returned = 'Returned',
  settled = 'Settled',
  unpaid = 'Unpaid',
  verified = 'Verified',
}

export enum ProjectStatus {
  planning = 'Planning',
  live = 'Live',
  closed = 'Closed',
}

// BOQ table headings

const baseHeadings = {
  number: {
    key: 'number',
    width: 'min-w-[85px] w-[6%] 4xl:min-w-[200px]',
  },
  item: {
    key: 'itemName',
    width: 'min-w-[166px] 3xl:w-[15%] 4xl:w-[10%]',
  },
  spec: {
    key: 'specification',
    width: 'min-w-[166px] w-[18%]! 3xl:w-[10%]!',
  },
};

export const claimHeadings = {
  ...baseHeadings,
  markup: {
    key: 'markup',
    width: 'min-w-[166px] w-[9%] 3xl:w-[15%]',
  },
  claim: {
    key: 'claimPeriod',
    width: 'min-w-[166px] w-[9%] 3xl:w-[15%]',
  },
  start: {
    key: 'start',
    width: 'min-w-[166px] w-[10%] 3xl:w-[14%]',
  },
  end: {
    key: 'end',
    width: 'min-w-[166px] w-[10%] 3xl:w-[14%]',
  },
};

// Context: in the claim view every column carries this width beside its own from `claimHeadings`, and
// Tailwind emits `w-[15%]` after the columns' `w-[6%]`, `w-[9%]` and `w-[10%]`, so it wins over them
// (only Spec's `w-[18%]!` holds). Cost Plus adds Markup %, and seven columns at 15% overflow the row,
// which wraps while the header squeezes (PLE-1932). The header and the rows both read it from here.
export const claimColumnWidth = (isCostPlus: boolean) => (isCostPlus ? 'w-[13%]' : 'w-[15%]');

// The claim view's minimum width, with room for Markup % (166px) on Cost Plus. At 1,200px a 13%
// column is under its 166px minimum, and the minimums alone overflow the row.
export const claimViewMinWidth = (isCostPlus: boolean) =>
  isCostPlus ? 'min-w-[1366px]' : 'min-w-[1200px]';

export const boqHeadings = {
  ...baseHeadings,
  vendor: {
    key: 'vendor',
    width: 'min-w-[160px] 3xl:w-[12%]',
  },
  reference: {
    key: 'reference',
    width: 'min-w-[160px] 3xl:w-[9%] 4xl:w-[10%]',
  },
  type: {
    key: 'type',
    width: 'min-w-[110px] 4xl:w-[10%]',
  },
  unit: {
    key: 'unit',
    width: 'min-w-[115px] 4xl:w-[10%]',
  },
  quantity: {
    key: 'quantity',
    width: 'min-w-[100px]',
  },
  rate: {
    key: 'rate',
    width: 'min-w-[100px]',
  },
  amount: {
    key: 'amount',
    width: 'min-w-[100px]',
  },
  remaining: {
    key: 'remaining',
    width: 'min-w-[100px]',
  },
};

/**
 * The symbol shown beside an amount the user is typing. `formatCurrency` keeps its own copy for
 * rendered figures — this one is for inputs, where the symbol sits outside the editable value.
 */
export const CURRENCY_SYMBOL = 'R';
